
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function CenturyCityPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Century City Painters - Professional Painting Services Cape Town";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Century City modern developments. Expert interior, exterior, and commercial painting for residential and office complexes. Get your free quote today.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Century City painters, modern development painting, residential complex painting, office painting, commercial painting, Cape Town painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/century-city`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Century City Painters - Professional Painting Services",
      "description": "Professional painting services for Century City modern developments. Expert interior, exterior, and commercial painting for residential and office complexes.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/century-city`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Century City",
        "description": "Modern painting services for Century City's contemporary residential and commercial developments.",
        "areaServed": "Century City, Cape Town",
        "serviceType": "Modern Development Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Century City')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-building-line',
      title: 'Residential Complex Painting',
      description: 'Professional painting services for Century City\'s modern apartment complexes and townhouse developments with contemporary finishes.',
      price: 'From R12,000',
      features: ['Modern color schemes', 'Durable finishes', 'Complex coordination', 'Minimal disruption']
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Commercial Office Painting',
      description: 'Expert painting solutions for Century City\'s business district including offices, retail spaces, and corporate facilities.',
      price: 'From R18,000',
      features: ['Professional appearance', 'After-hours work', 'Brand color matching', 'Quick turnaround']
    },
    {
      icon: 'ri-home-4-line',
      title: 'Modern Interior Design',
      description: 'Contemporary interior painting services that complement Century City\'s modern architectural style and urban lifestyle.',
      price: 'From R8,000',
      features: ['Trendy color palettes', 'Feature walls', 'Open plan optimization', 'Designer consultation']
    },
    {
      icon: 'ri-building-2-line',
      title: 'Exterior Maintenance',
      description: 'Comprehensive exterior painting and maintenance for Century City properties including weather protection and aesthetic enhancement.',
      price: 'From R15,000',
      features: ['Weather resistance', 'UV protection', 'Architectural enhancement', 'Long-term durability']
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Consultation',
      description: 'Professional color consultation services tailored to Century City\'s contemporary aesthetic and modern living requirements.',
      price: 'From R2,000',
      features: ['Modern trends', 'Space optimization', 'Light enhancement', 'Lifestyle matching']
    },
    {
      icon: 'ri-tools-line',
      title: 'Maintenance Programs',
      description: 'Ongoing maintenance programs for Century City developments including scheduled touch-ups and protective treatments.',
      price: 'From R6,000',
      features: ['Regular inspections', 'Preventive care', 'Emergency response', 'Cost-effective solutions']
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      property: 'Century City Apartment',
      rating: 5,
      text: 'Excellent work on our modern apartment. The team understood our contemporary style preferences and delivered a flawless finish that perfectly matches our urban lifestyle.',
      image: 'https://public.readdy.ai/ai/img_res/66bfcae75805dfb9337044d545a53908.jpg'
    },
    {
      name: 'Mark Johnson',
      property: 'Century City Office',
      rating: 5,
      text: 'Professional service for our business premises. They worked efficiently around our schedule and created a modern, professional environment that impresses our clients.',
      image: 'https://readdy.ai/api/search-image?query=Business%20professional%20man%20in%20modern%20Century%20City%20office%20space%20with%20freshly%20painted%20walls%2C%20contemporary%20workspace%2C%20satisfied%20client%20portrait&width=80&height=80&seq=century2&orientation=squarish'
    },
    {
      name: 'Lisa Chen',
      property: 'Century City Townhouse',
      rating: 5,
      text: 'Outstanding attention to detail and modern color selection. Our townhouse looks stunning and the quality of work exceeded our expectations for this premium location.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20homeowner%20woman%20in%20Century%20City%20townhouse%20with%20contemporary%20interior%20design%2C%20professionally%20painted%20walls%2C%20urban%20living%2C%20satisfied%20customer&width=80&height=80&seq=century3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Modern Apartment Complex',
      description: 'Complete interior and exterior painting for a 200-unit residential complex with contemporary color schemes and durable finishes',
      image: 'https://readdy.ai/api/search-image?query=Modern%20apartment%20complex%20in%20Century%20City%20with%20contemporary%20architecture%2C%20freshly%20painted%20exterior%2C%20urban%20development%2C%20professional%20painting%20work&width=400&height=300&seq=century4&orientation=landscape'
    },
    {
      title: 'Corporate Office Building',
      description: 'Professional painting services for a multi-story office building including reception areas, meeting rooms, and open workspaces',
      image: 'https://readdy.ai/api/search-image?query=Modern%20office%20building%20in%20Century%20City%20business%20district%2C%20professional%20painted%20exterior%2C%20contemporary%20commercial%20architecture%2C%20urban%20setting&width=400&height=300&seq=century5&orientation=landscape'
    },
    {
      title: 'Luxury Townhouse Development',
      description: 'High-end interior and exterior painting for exclusive townhouse development with custom color schemes and premium finishes',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20townhouse%20development%20in%20Century%20City%20with%20modern%20architecture%2C%20professionally%20painted%20exteriors%2C%20contemporary%20residential%20design&width=400&height=300&seq=century6&orientation=landscape'
    },
    {
      title: 'Retail Shopping Center',
      description: 'Commercial painting project for Century City shopping center including storefronts, common areas, and parking facilities',
      image: 'https://readdy.ai/api/search-image?query=Modern%20shopping%20center%20in%20Century%20City%20with%20freshly%20painted%20storefronts%2C%20contemporary%20retail%20architecture%2C%20professional%20commercial%20painting&width=400&height=300&seq=century7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20modern%20Century%20City%20development%20with%20contemporary%20architecture%2C%20painting%20equipment%2C%20scaffolding%2C%20urban%20setting%2C%20Cape%20Town%20skyline&width=1920&height=1080&seq=centuryhero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Century City
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Century City's modern developments. Specialized solutions for residential complexes, commercial offices, and contemporary properties with professional finishes that match the area's urban sophistication.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Century City Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Painting Excellence in Century City
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Century City represents Cape Town's premier mixed-use development, combining residential, commercial, and retail spaces in a modern urban environment. Our specialized painting services cater to the unique requirements of this contemporary development, from high-rise apartments to corporate offices and retail establishments.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Located in the heart of Cape Town's northern suburbs, Century City demands painting solutions that match its modern aesthetic and urban lifestyle. We provide comprehensive services using premium materials and contemporary techniques that enhance the architectural beauty while ensuring long-lasting durability in the urban environment.
              </p>
              <p className="text-lg text-gray-600">
                Our experience with Century City's diverse property types, from residential complexes to commercial buildings, ensures we deliver results that meet the high standards expected in this prestigious development while accommodating the busy urban lifestyle of residents and businesses.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Century%20City%20development%20overview%20with%20modern%20buildings%2C%20residential%20and%20commercial%20properties%2C%20contemporary%20urban%20architecture%2C%20Cape%20Town%20setting&width=600&height=400&seq=century8&orientation=landscape"
                alt="Century City Development"
                className="w-full h-96 object-cover object-center rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Century City Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed for Century City's modern residential and commercial developments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mr-2" style={{backgroundColor: '#ffff00'}}>
                        <i className={`ri-check-line text-black text-2xl`}></i>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Century City Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in modern development painting with recent successful projects in Century City.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-center"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Century City Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Century City property owners who have experienced our exceptional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Century City Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's premier painting specialists for Century City. Our expert team is ready to provide you with a comprehensive consultation and detailed quote for your modern development property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
